import { useState } from 'react';
import { LayoutTemplate, Save, X } from 'lucide-react';

export interface SaveAsNewTemplateCardProps {
  /**
   * Suggested default name for the template. Callers typically derive this
   * from the import name the user entered on step 1 — keeps the UX tight
   * because they already named the run.
   */
  defaultName?: string;
  /** Suggested default description. Same reasoning as defaultName. */
  defaultDescription?: string;
  /** Whether a save operation is currently in flight. Disables the buttons. */
  saving?: boolean;
  /** Surface the most recent save error inline. */
  error?: string | null;
  /**
   * User confirmed — create a fresh top-level template with the current
   * run's mappings + match key + data mode. Caller sets name/description/
   * dataMode/fieldMappings/matchKeyField/importBehavior on its side.
   */
  onSave: (name: string, description: string) => void | Promise<void>;
  /** User picked "Don't save". Caller should hide the card; run stays committed. */
  onDismiss: () => void;
}

/**
 * Card shown after a successful import run that was NOT based on an existing
 * template (i.e. the user walked in with a file, mapped columns inline, and
 * committed). Lets them preserve the mapping work as a brand-new independent
 * template in one click so they don't have to redo it next time.
 *
 * This is the "gold star" flow: walk in with a shapefile, import it, and
 * save the import as a template in the same pass. Sibling to
 * SaveAsVariantCard (which handles the shell + variant case); kept as a
 * separate component because the UX is simpler and the persistence call
 * has no parentTemplateId linkage.
 */
export default function SaveAsNewTemplateCard({
  defaultName = '',
  defaultDescription = '',
  saving = false,
  error,
  onSave,
  onDismiss,
}: SaveAsNewTemplateCardProps) {
  const [name, setName] = useState<string>(defaultName);
  const [description, setDescription] = useState<string>(defaultDescription);

  const canSave = !saving && name.trim().length > 0;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave(name.trim(), description.trim());
  };

  return (
    <div
      className="mt-6 border-2 border-blue-200 bg-blue-50 rounded-lg p-5"
      data-testid="save-as-new-template-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-md bg-blue-100 border border-blue-200 flex items-center justify-center">
          <LayoutTemplate className="w-5 h-5 text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900">
            Save this import as a template
          </div>
          <p className="text-xs text-gray-600 mt-0.5">
            Your column mappings, match key, and import behavior will be reusable
            next time you import a similar file. Skipping here still commits the
            current run — only the mapping work is discarded.
          </p>

          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Template name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Atwater Shapefile Parcels"
                disabled={saving}
                data-testid="save-as-new-template-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                placeholder="Short note for future you — e.g. 'Merced County shapefile export format, 2026 schema'"
                disabled={saving}
                data-testid="save-as-new-template-description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
          </div>

          {error && (
            <div
              className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="save-as-new-template-error"
            >
              {error}
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onDismiss}
              disabled={saving}
              data-testid="save-as-new-template-dismiss"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" />
              Don't save
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSave}
              data-testid="save-as-new-template-save"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
